import React from 'react';
import { Download, AlertTriangle } from 'lucide-react';

const CRITERIA = [
  { key: 'documentation', label: 'Documentation', description: 'Disponibilité et accessibilité de la documentation technique' },
  { key: 'demontabilite', label: 'Démontabilité', description: 'Facilité de démontage et de remontage du produit' },
  { key: 'pieces_dispo', label: 'Disponibilité des pièces', description: 'Disponibilité des pièces détachées sur le marché' },
  { key: 'prix_pieces', label: 'Prix des pièces', description: 'Rapport entre le prix des pièces et le prix du produit' },
  { key: 'logiciel', label: 'Support logiciel', description: 'Durée de disponibilité des mises à jour logicielles' },
];

const DEFAULT_SCORES = {
  documentation: 5,
  demontabilite: 5,
  pieces_dispo: 5,
  prix_pieces: 5,
  logiciel: 5,
};

function scoreColor(score) {
  if (score >= 7) return { bg: 'bg-green-500', text: 'text-emerald-600', hex: '#22c55e' };
  if (score >= 4) return { bg: 'bg-yellow-500', text: 'text-yellow-400', hex: '#eab308' };
  return { bg: 'bg-red-500', text: 'text-rose-600', hex: '#ef4444' };
}

function generatePdfHtml(scores, productName) {
  const avg = (Object.values(scores).reduce((a, b) => a + b, 0) / 5).toFixed(1);
  const color = parseFloat(avg) >= 7 ? '#22c55e' : parseFloat(avg) >= 4 ? '#eab308' : '#ef4444';
  const criteriaRows = CRITERIA.map(c => {
    const val = scores[c.key];
    const pct = (val / 10) * 100;
    const barColor = val >= 7 ? '#22c55e' : val >= 4 ? '#eab308' : '#ef4444';
    return `
      <tr>
        <td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;font-weight:500;">${c.label}</td>
        <td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;width:50%;">
          <div style="background:#e5e7eb;border-radius:999px;height:12px;overflow:hidden;">
            <div style="background:${barColor};height:100%;width:${pct}%;border-radius:999px;transition:width 0.3s;"></div>
          </div>
        </td>
        <td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:600;color:${barColor};">${val}/10</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Indice de réparabilité – ${productName || 'Produit'}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', sans-serif; color: #1f2937; background: #fff; }
    .header { background: linear-gradient(135deg, #166534, #22c55e); color: white; padding: 40px; }
    .header h1 { font-family: 'Outfit', sans-serif; font-size: 28px; font-weight: 700; margin-bottom: 4px; }
    .header p { opacity: 0.9; font-size: 14px; }
    .brand { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 600; margin-bottom: 16px; }
    .content { padding: 40px; max-width: 800px; margin: 0 auto; }
    .score-block { text-align: center; margin: 32px 0; padding: 24px; border: 2px solid ${color}; border-radius: 16px; }
    .score-value { font-family: 'Outfit', sans-serif; font-size: 64px; font-weight: 700; color: ${color}; }
    .score-label { font-size: 14px; color: #6b7280; margin-top: 4px; }
    .product-name { font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 600; margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 24px 0; }
    table th { text-align: left; padding: 12px 16px; background: #f9fafb; border-bottom: 2px solid #e5e7eb; font-size: 13px; text-transform: uppercase; color: #6b7280; letter-spacing: 0.05em; }
    .legal { margin-top: 32px; padding: 20px; background: #f9fafb; border-radius: 12px; font-size: 12px; color: #6b7280; line-height: 1.6; }
    .legal strong { color: #374151; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #9ca3af; }
    @media print { .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">Atelier Météris</div>
    <h1>Indice de réparabilité</h1>
    <p>Conformément à la Loi AGEC (anti-gaspillage pour une économie circulaire)</p>
  </div>
  <div class="content">
    ${productName ? `<p class="product-name">${productName}</p>` : ''}
    <div class="score-block">
      <div class="score-value">${avg}</div>
      <div class="score-label">sur 10</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Critère</th>
          <th>Niveau</th>
          <th style="text-align:center;">Note</th>
        </tr>
      </thead>
      <tbody>
        ${criteriaRows}
      </tbody>
    </table>
    <div class="legal">
      <strong>Loi AGEC – Article L. 541-9-2 du Code de l'environnement</strong><br/>
      L'indice de réparabilité informe le consommateur sur le caractère plus ou moins réparable
      de ses achats de produits électriques et électroniques. Cet indice est calculé selon les critères
      définis par le décret n° 2020-1757 du 29 décembre 2020 : documentation technique, démontabilité,
      disponibilité des pièces détachées, prix des pièces détachées et support logiciel.
    </div>
    <div class="footer">
      Atelier Météris – 8 rue Georges Claude, 77100 Meaux<br/>
      Document généré le ${new Date().toLocaleDateString('fr-FR')}
    </div>
  </div>
</body>
</html>`;
}

function openPrintableHtml(htmlContent) {
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (win) {
    win.addEventListener('afterprint', () => URL.revokeObjectURL(url));
    win.addEventListener('load', () => setTimeout(() => win.print(), 300));
  }
}

export default function RepairabilityPanel({
  scores = DEFAULT_SCORES,
  onScoresChange,
  status = null,
  onStatusChange,
  productName = '',
  showPdfButton = true,
  compact = false,
}) {
  const isEnCours = status === 'en_cours_analyse';
  const avg = (Object.values(scores).reduce((a, b) => a + b, 0) / 5).toFixed(1);
  const color = scoreColor(parseFloat(avg));

  const handleSliderChange = (key, value) => {
    if (onScoresChange) {
      onScoresChange({ ...scores, [key]: parseInt(value, 10) });
    }
  };

  const handleEnCoursToggle = (checked) => {
    if (onStatusChange) {
      onStatusChange(checked ? 'en_cours_analyse' : (parseFloat(avg) > 0 ? 'scored' : null));
    }
  };

  const handleGeneratePdf = () => {
    const html = generatePdfHtml(scores, productName);
    openPrintableHtml(html);
  };

  return (
    <div className={`bg-white rounded-sm border border-slate-200 ${compact ? 'p-4' : 'p-6'} space-y-4`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-primary">
          Indice de réparabilité
        </h3>
        {!isEnCours ? (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${color.bg}/15`}>
            <span className={`text-lg font-bold ${color.text}`}>{avg}</span>
            <span className="text-xs text-slate-600">/10</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10">
            <AlertTriangle className="w-4 h-4 text-secondary" />
            <span className="text-xs font-medium text-secondary">En cours d'analyse</span>
          </div>
        )}
      </div>

      {/* En cours d'analyse checkbox */}
      <label className="flex items-center gap-3 cursor-pointer group">
        <div className="relative">
          <input
            type="checkbox"
            checked={isEnCours}
            onChange={(e) => handleEnCoursToggle(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-5 h-5 rounded border border-slate-300 bg-slate-50 peer-checked:bg-secondary peer-checked:border-secondary flex items-center justify-center transition-colors">
            {isEnCours && (
              <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">
          En cours d'analyse (indice non encore calculé)
        </span>
      </label>

      {/* Criteria sliders */}
      <div className={`space-y-3 ${isEnCours ? 'opacity-40 pointer-events-none' : ''}`}>
        {CRITERIA.map((c) => {
          const val = scores[c.key] ?? 5;
          const valColor = scoreColor(val);
          return (
            <div key={c.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-600">{c.label}</label>
                <span className={`text-xs font-semibold ${valColor.text}`}>{val}/10</span>
              </div>
              {!compact && (
                <p className="text-[10px] text-slate-500 mb-1.5">{c.description}</p>
              )}
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={val}
                onChange={(e) => handleSliderChange(c.key, e.target.value)}
                disabled={isEnCours}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-secondary [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(245,158,11,0.2)]
                  [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
                  [&::-moz-range-thumb]:bg-secondary [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${valColor.hex} 0%, ${valColor.hex} ${val * 10}%, #1f2937 ${val * 10}%, #1f2937 100%)`,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* PDF button */}
      {showPdfButton && !isEnCours && (
        <button
          type="button"
          onClick={handleGeneratePdf}
          className="flex items-center gap-2 text-sm text-secondary hover:text-secondary transition-colors mt-2"
        >
          <Download className="w-4 h-4" />
          Générer la fiche réparabilité (PDF)
        </button>
      )}
    </div>
  );
}
